import React from 'react';
import {
    Phone,
    Mail,
    MessageCircle,
    Folder,
    FileText,
    BookOpen,
    Book,
    ExternalLink,
    Undo2,
    type LucideIcon,
} from 'lucide-react';
import { Avatar } from './Avatar';
import { parseFormattedText, cn, trackAnalytics, sendGtagEvent } from '@/lib/utils';
import type { ActionBlock as ActionBlockType } from '@/types';

interface ActionBlockProps {
    message: ActionBlockType;
    botAvatar: string;
    botId?: string;
    sessionId?: string;
    apiBaseUrl?: string;
    customStyles?: {
        primaryColor?: string;
        secondaryColor?: string;
        botMessageBg?: string;
        borderRadius?: number;
        fontSize?: number;
    };
    onUndo?: () => void;
}

// Map icon names to Lucide icons
const iconMap: Record<string, LucideIcon> = {
    'phone': Phone,
    'mail': Mail,
    'message-circle': MessageCircle,
    'folder': Folder,
    'file-text': FileText,
    'book-open': BookOpen,
    'book': Book,
};

const getGridClass = (showPerRow: number) => {
    const gridClasses: Record<number, string> = {
        1: 'action-grid-1',
        2: 'action-grid-2',
        3: 'action-grid-3',
    };
    return gridClasses[Math.min(Math.max(showPerRow, 1), 3)] || 'action-grid-2';
};

export const ActionBlock: React.FC<ActionBlockProps> = ({ message, botAvatar, botId, sessionId, apiBaseUrl, customStyles, onUndo }) => {
    const primaryColor = customStyles?.primaryColor || '#a855f7';
    const secondaryColor = customStyles?.secondaryColor || '#764ba2';
    const botBgColor = customStyles?.botMessageBg || secondaryColor || '#ffffff';
    const borderRadius = customStyles?.borderRadius || 16;
    const fontSize = customStyles?.fontSize ?? 14;
    const showBackground = message.showBackground ?? false;

    const handleButtonClick = (url: string, buttonText: string) => {
        if (botId && sessionId) {
            void trackAnalytics(botId, 'action_button_click', sessionId, apiBaseUrl, buttonText);
            // The other three events have always gone to the customer's GA4 as
            // well as to us; this one only ever reached us, so a customer looking
            // at their own analytics saw a funnel with a step missing.
            sendGtagEvent('action_button_click_zentri', botId, { button_text: buttonText });
        }
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
        <div className="zentri-flex zentri-items-start zentri-gap-3 zentri-mb-4 zentri-animate-fade-in">
            <Avatar src={botAvatar} alt="Bot" size="md" />

            <div className="zentri-flex zentri-flex-col zentri-gap-3 zentri-max-w-lg zentri-group">
                {/* Optional title */}
                {message.title && (
                    <div
                        className="zentri-px-3.5 zentri-py-2.5 zentri-flex zentri-items-center zentri-justify-between zentri-animate-fade-slide-up zentri-transition-all zentri-duration-300 zentri-border zentri-border-neutral-100"
                        style={{
                            backgroundColor: botBgColor,
                            borderRadius: `${borderRadius}px`,
                            borderTopLeftRadius: '0',
                            borderBottomRightRadius: `${borderRadius}px`,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                        }}
                    >
                        <div
                            className="zentri-leading-relaxed zentri-font-semibold zentri-text-gray-800"
                            style={{
                                color: '#1f2937',
                                fontSize: `${fontSize}px`,
                                ['--zentri-primary' as string]: primaryColor,
                            }}
                            dangerouslySetInnerHTML={parseFormattedText(message.title)}
                        />
                        {onUndo && (
                            <button
                                onClick={onUndo}
                                className="zentri-opacity-0 zentri-group-hover:zentri-opacity-100 zentri-transition-all zentri-duration-300 zentri-p-1 zentri-rounded-md hover:zentri-bg-neutral-100 zentri-text-neutral-500 hover:zentri-text-neutral-700 zentri-flex-shrink-0 zentri-ml-2 hover:zentri-scale-110"
                                title="Undo message"
                                aria-label="Undo message"
                            >
                                <Undo2 size={14} strokeWidth={2} />
                            </button>
                        )}
                    </div>
                )}

                {/* Action buttons - optional secondaryColor card background with padding */}
                <div
                    className={cn(
                        'zentri-animate-fade-slide-up zentri-transition-all zentri-duration-300 zentri-shadow-none',
                        showBackground && 'zentri-rounded-xl'
                    )}
                    style={showBackground ? { backgroundColor: secondaryColor, padding: '10px' } : undefined}
                >
                    <div className={getGridClass(message.showPerRow)}>
                        {message.buttons.map((button, index) => {
                            const IconComponent = button.icon ? iconMap[button.icon] : null;
                            const customStyle = button.customStyle || {};
                            const hasCustomBg = !!customStyle.backgroundColor;
                            const useSecondaryBg = showBackground && !hasCustomBg;

                            return (
                                <button
                                    key={index}
                                    onClick={() => handleButtonClick(button.url, button.text)}
                                    className={cn(
                                        'action-button',
                                        'zentri-group zentri-relative',
                                        'zentri-flex zentri-items-center zentri-justify-center zentri-gap-2.5',
                                        'zentri-px-4 zentri-py-3 zentri-rounded-xl',
                                        'zentri-border-2 zentri-font-medium zentri-text-[12px]',
                                        'zentri-transition-all zentri-duration-300 zentri-ease-out',
                                        'focus:zentri-outline-none focus:zentri-ring-2 focus:zentri-ring-brand-500 focus:zentri-ring-offset-0',
                                        'zentri-animate-fade-slide-up zentri-w-full',
                                        'zentri-cursor-pointer zentri-min-h-[48px]',
                                        'hover:zentri-shadow-elegant active:zentri-scale-[0.98]',
                                        hasCustomBg || useSecondaryBg ? '' : 'hover:zentri-bg-brand-50'
                                    )}
                                    style={{
                                        backgroundColor: customStyle.backgroundColor || (useSecondaryBg ? secondaryColor : '#ffffff'),
                                        borderColor: customStyle.borderColor || customStyle.backgroundColor || (useSecondaryBg ? secondaryColor : primaryColor),
                                        color: customStyle.textColor || (customStyle.backgroundColor || useSecondaryBg ? '#ffffff' : primaryColor),
                                        animationDelay: `${index * 0.08}s`,
                                        ...(showBackground && !hasCustomBg ? { padding: '10px 16px' } : {}),
                                    }}
                                >
                                    {IconComponent && (
                                        <IconComponent
                                            size={16}
                                            strokeWidth={2.5}
                                            className="zentri-flex-shrink-0"
                                            style={{ color: customStyle.iconColor || customStyle.textColor || (customStyle.backgroundColor || useSecondaryBg ? '#ffffff' : primaryColor) }}
                                        />
                                    )}
                                    <span className="zentri-font-medium zentri-leading-tight">{button.text}</span>
                                    {!IconComponent && (
                                        <ExternalLink
                                            size={16}
                                            strokeWidth={2.5}
                                            className="zentri-opacity-70 zentri-flex-shrink-0"
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};
